@import 'tailwindcss';

/*
 * Design tokens. Exposed to Tailwind as utilities (bg-surface-raised,
 * text-accent, rounded-lg, …) and as plain CSS variables, which the
 * react-day-picker and ag-grid themes read directly.
 */
@theme {
  --color-bg: #1a1a1c;
  --color-surface: #1c1c1e;
  --color-surface-raised: #262628;
  --color-surface-elevated: #333335;

  --color-fg: #e4e4e4;
  --color-fg-muted: rgba(228, 228, 228, 0.75);
  --color-fg-subtle: rgba(228, 228, 228, 0.5);

  --color-accent: #c2710f;
  --color-accent-bright: #d4a041;
  --color-accent-soft: rgba(194, 113, 15, 0.12);
  --color-accent-softer: rgba(194, 113, 15, 0.07);
  --color-accent-strong: rgba(194, 113, 15, 0.2);

  --color-line: rgba(255, 255, 255, 0.08);
  --color-card: rgba(255, 255, 255, 0.04);
  --color-card-line: rgba(255, 255, 255, 0.04);
  --color-card-line-top: rgba(255, 255, 255, 0.08);

  --color-danger: #ff6b6b;
  --color-danger-soft: rgba(255, 107, 107, 0.08);
  --color-danger-line: rgba(255, 107, 107, 0.25);
  --color-success: #2ecc71;
  --color-warning: #e4b86a;

  --radius-sm: 6px;
  --radius-md: 12px;
  --radius-lg: 20px;
  --radius-xl: 24px;

  --shadow-soft: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-medium: 0 8px 30px rgba(0, 0, 0, 0.3);
  --shadow-sheet: 0 -4px 30px rgba(0, 0, 0, 0.6);

  --font-sans: 'Inter', system-ui, -apple-system, sans-serif;

  --animate-fade-in: fade-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
  --animate-slide-down: slide-down 0.3s ease-out both;
  --animate-slide-up: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
  --animate-indeterminate: indeterminate 1.1s ease-in-out infinite;
  --animate-spin-slow: spin 1s linear infinite;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(400%);
  }
}

/*
 * Legacy aliases. ag-grid's theme params and a few third-party overrides still
 * reference these names; keeping them mapped to the tokens above means there
 * is exactly one place to change a colour.
 */
:root {
  --background-primary: var(--color-bg);
  --background-secondary: var(--color-surface);
  --background-tertiary: var(--color-surface-raised);
  --background-elevated: var(--color-surface-elevated);
  --text-primary: var(--color-fg);
  --text-secondary: var(--color-fg-muted);
  --text-tertiary: var(--color-fg-subtle);
  --accent-primary: var(--color-accent);
  --border-color: var(--color-line);
}

@layer base {
  html,
  body {
    height: 100%;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    position: relative;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  body {
    font-family: var(--font-sans);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--color-bg);
    color: var(--color-fg);
    line-height: 1.6;
  }

  #root {
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
  }

  button {
    cursor: pointer;
    font: inherit;
    color: inherit;
  }

  input,
  select,
  textarea {
    font: inherit;
  }

  :focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    html {
      font-size: 15px;
    }
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--color-bg);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-surface-raised);
    border-radius: 4px;
    border: 2px solid var(--color-bg);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--color-accent);
  }
}

@layer utilities {
  .hide-scrollbar {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
